import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import Layout from '../components/Layout';
import { FaCheck, FaTimes, FaClock, FaHourglassHalf } from 'react-icons/fa';
import { formatRupiah } from '../utils/formatter';

const DataPeminjaman = () => {
    const [peminjaman, setPeminjaman] = useState([]);
    const [pendingPeminjaman, setPendingPeminjaman] = useState([]);
    const [buku, setBuku] = useState([]);
    const [anggota, setAnggota] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('pending'); // 'pending' or 'all'

    // State untuk search dan pagination
    const [searchTerm, setSearchTerm] = useState('');
    const [entriesPerPage, setEntriesPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);

    // State untuk modal verifikasi
    const [isVerifikasiModalOpen, setIsVerifikasiModalOpen] = useState(false);
    const [selectedPeminjamanId, setSelectedPeminjamanId] = useState(null);
    const [verifikasiData, setVerifikasiData] = useState({
        kondisi_buku: 'baik',
        catatan_admin: ''
    });

    // State untuk modal reject
    const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
    const [rejectReason, setRejectReason] = useState('');

    const [formData, setFormData] = useState({
        id_anggota: '',
        id_buku: '',
        tanggal_peminjaman: '',
        tanggal_kembali: ''
    });

    const fetchPeminjaman = async () => {
        try {
            // ✅ PERBAIKAN: Auto-expire dulu sebelum fetch
            await axios.post(`${process.env.REACT_APP_API_URL}/peminjaman/expire-pending`);
            
            const [allResponse, pendingResponse] = await Promise.all([
                axios.get(`${process.env.REACT_APP_API_URL}/peminjaman`),
                axios.get(`${process.env.REACT_APP_API_URL}/peminjaman/pending`)
            ]);
            setPeminjaman(allResponse.data);
            setPendingPeminjaman(pendingResponse.data);
        } catch (error) {
            Swal.fire('Gagal', 'Gagal memuat data peminjaman. Silakan coba lagi.', 'error');
        } finally {
            setLoading(false);
        }
    };

    const fetchBuku = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/buku`);
            setBuku(response.data);
        } catch (error) {
            console.error('Failed to fetch books:', error);
        }
    };

    const fetchAnggota = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/anggota`);
            setAnggota(response.data);
        } catch (error) {
            console.error('Failed to fetch members:', error);
        }
    };

    useEffect(() => {
        fetchPeminjaman();
        fetchBuku();
        fetchAnggota();
    }, []);

    // Filter data based on active tab
    const dataToDisplay = activeTab === 'pending' ? pendingPeminjaman : peminjaman;

    // Filter data berdasarkan search term
    const filteredPeminjaman = dataToDisplay.filter(item =>
        item.nama_anggota.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.judul_buku.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (item.nis && item.nis.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    // Pagination logic
    const totalEntries = filteredPeminjaman.length;
    const totalPages = Math.ceil(totalEntries / entriesPerPage);
    const startIndex = (currentPage - 1) * entriesPerPage;
    const endIndex = startIndex + entriesPerPage;
    const currentEntries = filteredPeminjaman.slice(startIndex, endIndex);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, entriesPerPage, activeTab]);

    const handleApprovePeminjaman = async (id, namaAnggota) => {
        const result = await Swal.fire({
            title: 'Setujui Peminjaman?',
            text: `Anda akan menyetujui peminjaman untuk ${namaAnggota}`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#48c78e',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Ya, Setujui!',
            cancelButtonText: 'Batal'
        });

        if (result.isConfirmed) {
            try {
                await axios.put(`${process.env.REACT_APP_API_URL}/peminjaman/approve/${id}`, {
                    keterangan_admin: 'Disetujui oleh admin'
                });
                Swal.fire('Berhasil!', 'Peminjaman berhasil disetujui!', 'success');
                fetchPeminjaman();
            } catch (error) {
                Swal.fire('Gagal', error.response?.data?.message || 'Gagal menyetujui peminjaman.', 'error');
            }
        }
    };

    const handleRejectPeminjaman = (id) => {
        setSelectedPeminjamanId(id);
        setRejectReason('');
        setIsRejectModalOpen(true);
    };

    const submitReject = async () => {
        if (!rejectReason.trim()) {
            Swal.fire('Peringatan', 'Keterangan penolakan wajib diisi!', 'warning');
            return;
        }

        try {
            await axios.put(`${process.env.REACT_APP_API_URL}/peminjaman/reject/${selectedPeminjamanId}`, {
                keterangan_admin: rejectReason
            });
            Swal.fire('Berhasil!', 'Peminjaman berhasil ditolak.', 'success');
            setIsRejectModalOpen(false);
            fetchPeminjaman();
        } catch (error) {
            Swal.fire('Gagal', error.response?.data?.message || 'Gagal menolak peminjaman.', 'error');
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    const handleEntriesPerPageChange = (e) => {
        setEntriesPerPage(parseInt(e.target.value));
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleAddClick = () => {
        setFormData({
            id_anggota: '',
            id_buku: '',
            tanggal_peminjaman: new Date().toISOString().split('T')[0],
            tanggal_kembali: ''
        });
        setIsModalOpen(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`${process.env.REACT_APP_API_URL}/peminjaman`, formData);
            Swal.fire('Berhasil', 'Peminjaman berhasil ditambahkan!', 'success');
            setIsModalOpen(false);
            fetchPeminjaman();
        } catch (error) {
            Swal.fire('Gagal', error.response?.data?.message || 'Gagal menyimpan data peminjaman.', 'error');
        }
    };

    const handleVerifikasiPengembalian = (id) => {
        setSelectedPeminjamanId(id);
        setVerifikasiData({
            kondisi_buku: 'baik',
            catatan_admin: ''
        });
        setIsVerifikasiModalOpen(true);
    };

    const handleSubmitVerifikasi = async () => {
        try {
            const response = await axios.post(`${process.env.REACT_APP_API_URL}/peminjaman/pengembalian/${selectedPeminjamanId}`, {
                keterangan: 'Buku telah dikembalikan dan diverifikasi oleh admin',
                kondisi_buku: verifikasiData.kondisi_buku,
                catatan_admin: verifikasiData.catatan_admin
            });

            const denda = response.data.denda;
            const kondisi = response.data.kondisi_buku;

            let message = `Verifikasi berhasil. Kondisi buku: ${kondisi}.`;
            if (denda > 0) {
                message += ` Denda keterlambatan: ${formatRupiah(denda)}.`;
            } else {
                message += ` Tidak ada denda.`;
            }

            Swal.fire('Berhasil!', message, 'success');
            setIsVerifikasiModalOpen(false);
            fetchPeminjaman();
        } catch (error) {
            console.error('Error verifying pengembalian:', error);
            Swal.fire('Gagal', 'Gagal memproses verifikasi pengembalian.', 'error');
        }
    };

    const renderPagination = () => {
        const pages = [];
        for (let i = 1; i <= totalPages; i++) {
            pages.push(
                <li key={i}>
                    <button
                        className={`pagination-link ${i === currentPage ? 'is-current' : ''}`}
                        onClick={() => handlePageChange(i)}
                    >
                        {i}
                    </button>
                </li>
            );
        }
        return pages;
    };

    const getStatusColor = (status_persetujuan, status_peminjaman) => {
        if (status_persetujuan === 'pending') return 'is-warning';
        if (status_persetujuan === 'rejected' || status_persetujuan === 'expired') return 'is-danger';
        if (status_peminjaman === 'Dipinjam') return 'is-info';
        if (status_peminjaman === 'Diajukan Pengembalian') return 'is-link';
        if (status_peminjaman === 'Selesai') return 'is-success';
        return 'is-light';
    };

    const getStatusText = (item) => {
        if (item.status_persetujuan === 'pending') {
            const jamMenunggu = item.jam_menunggu || 0;
            return `Menunggu Verifikasi (${jamMenunggu}h)`;
        }
        if (item.status_persetujuan === 'expired') return 'Expired';
        if (item.status_persetujuan === 'rejected') return 'Ditolak';
        return item.status_peminjaman;
    };

    if (loading) {
        return (
            <Layout title="Data Peminjaman">
                <p>Memuat data peminjaman...</p>
            </Layout>
        );
    }

    return (
        <Layout title="Data Peminjaman">
            <div className="box">
                {/* Tabs */}
                <div className="tabs is-boxed">
                    <ul>
                        <li className={activeTab === 'pending' ? 'is-active' : ''}>
                            <a onClick={() => setActiveTab('pending')}>
                                <FaClock className="mr-2" />
                                <span>Menunggu Persetujuan ({pendingPeminjaman.length})</span>
                            </a>
                        </li>
                        <li className={activeTab === 'all' ? 'is-active' : ''}>
                            <a onClick={() => setActiveTab('all')}>
                                <span>Semua Peminjaman</span>
                            </a>
                        </li>
                    </ul>
                </div>

                <div className="level">
                    <div className="level-left">
                        <div className="level-item">
                            <div className="field has-addons">
                                <p className="control">
                                    <span className="select">
                                        <select value={entriesPerPage} onChange={handleEntriesPerPageChange}>
                                            <option value={10}>10</option>
                                            <option value={25}>25</option>
                                            <option value={50}>50</option>
                                            <option value={100}>100</option>
                                        </select>
                                    </span>
                                </p>
                                <p className="control">
                                    <span className="button is-static">entri</span>
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="level-right">
                        <div className="level-item">
                            <div className="control has-icons-right">
                                <input
                                    className="input"
                                    type="text"
                                    placeholder="Cari..."
                                    value={searchTerm}
                                    onChange={handleSearchChange}
                                    style={{ minWidth: '250px' }}
                                />
                                <span className="icon is-small is-right">
                                    <i className="fas fa-search"></i>
                                </span>
                            </div>
                        </div>
                        <div className="level-item">
                            <button className="button is-info" onClick={handleAddClick}>
                                <i className="fas fa-plus mr-2"></i> Tambah Peminjaman
                            </button>
                        </div>
                    </div>
                </div>

                {activeTab === 'pending' && pendingPeminjaman.length > 0 && (
                    <div className="notification is-warning is-light mb-4">
                        <strong>⚠️ Perhatian:</strong> Ada {pendingPeminjaman.length} pengajuan peminjaman yang menunggu persetujuan Anda. Pengajuan akan otomatis expired dalam 24 jam.
                    </div>
                )}

                <table className="table is-fullwidth is-striped is-hoverable">
                    <thead>
                        <tr>
                            <th>No</th>
                            <th>NIS</th>
                            <th>Nama Anggota</th>
                            <th>Judul Buku</th>
                            <th>Tanggal Pengajuan</th>
                            <th>Tanggal Pinjam</th>
                            <th>Tanggal Kembali</th>
                            <th>Status</th>
                            <th>Aksi</th>
                        </tr>
                    </thead>
                    <tbody>
                        {currentEntries.length > 0 ? (
                            currentEntries.map((item, index) => (
                                <tr key={item.id}>
                                    <td>{startIndex + index + 1}</td>
                                    <td>{item.nis}</td>
                                    <td>{item.nama_anggota}</td>
                                    <td>{item.judul_buku}</td>
                                    <td>
                                        {item.tanggal_pengajuan 
                                            ? new Date(item.tanggal_pengajuan).toLocaleString('id-ID')
                                            : '-'}
                                    </td>
                                    <td>{new Date(item.tanggal_peminjaman).toLocaleDateString('id-ID')}</td>
                                    <td>{new Date(item.tanggal_kembali).toLocaleDateString('id-ID')}</td>
                                    <td>
                                        <span className={`tag ${getStatusColor(item.status_persetujuan, item.status_peminjaman)}`}>
                                            {getStatusText(item)}
                                        </span>
                                    </td>
                                    <td>
                                        {/* ✅ PERBAIKAN: Aksi untuk tab pending */}
                                        {activeTab === 'pending' && item.status_persetujuan === 'pending' && (
                                            <div className="buttons are-small">
                                                <button
                                                    className="button is-success is-small"
                                                    onClick={() => handleApprovePeminjaman(item.id, item.nama_anggota)}
                                                    title="Setujui"
                                                >
                                                    <FaCheck />
                                                </button>
                                                <button
                                                    className="button is-danger is-small"
                                                    onClick={() => handleRejectPeminjaman(item.id)}
                                                    title="Tolak"
                                                >
                                                    <FaTimes />
                                                </button>
                                            </div>
                                        )}

                                        {/* ✅ PERBAIKAN: Aksi untuk tab all - hanya verifikasi pengembalian */}
                                        {activeTab === 'all' && item.status_peminjaman === 'Diajukan Pengembalian' && item.status_persetujuan === 'approved' && (
                                            <button
                                                className="button is-success is-small"
                                                onClick={() => handleVerifikasiPengembalian(item.id)}
                                            >
                                                Verifikasi Pengembalian
                                            </button>
                                        )}

                                        {/* ✅ PERBAIKAN: Tampilkan keterangan untuk rejected/expired */}
                                        {activeTab === 'all' && (item.status_persetujuan === 'rejected' || item.status_persetujuan === 'expired') && item.keterangan_admin && (
                                            <div className="content">
                                                <span className="tag is-danger is-light" title={item.keterangan_admin}>
                                                    <i className="fas fa-info-circle mr-1"></i>
                                                    {item.keterangan_admin.length > 30
                                                        ? item.keterangan_admin.substring(0, 30) + '...'
                                                        : item.keterangan_admin}
                                                </span>
                                            </div>
                                        )}

                                        {/* ✅ PERBAIKAN: Tidak ada aksi untuk status lainnya di tab all */}
                                        {activeTab === 'all' && 
                                         item.status_peminjaman !== 'Diajukan Pengembalian' && 
                                         item.status_persetujuan !== 'rejected' && 
                                         item.status_persetujuan !== 'expired' && (
                                            <span className="tag is-light">-</span>
                                        )}
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="9" className="has-text-centered">
                                    {searchTerm ? 'Tidak ada data yang sesuai dengan pencarian.' : 
                                     activeTab === 'pending' ? 'Tidak ada pengajuan peminjaman yang menunggu.' :
                                     'Tidak ada data peminjaman.'}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>

                {totalPages > 1 && (
                    <nav className="pagination is-centered mt-4" role="navigation" aria-label="pagination">
                        <button 
                            className="pagination-previous" 
                            onClick={() => handlePageChange(currentPage - 1)} 
                            disabled={currentPage === 1}
                        >
                            Previous
                        </button>
                        <button 
                            className="pagination-next" 
                            onClick={() => handlePageChange(currentPage + 1)} 
                            disabled={currentPage === totalPages}
                        >
                            Next
                        </button>
                        <ul className="pagination-list">
                            {renderPagination()}
                        </ul>
                    </nav>
                )}
            </div>

            {/* Modal Tambah Peminjaman */}
            <div className={`modal ${isModalOpen ? 'is-active' : ''}`}>
                <div className="modal-background" onClick={() => setIsModalOpen(false)}></div>
                <div className="modal-card">
                    <header className="modal-card-head">
                        <p className="modal-card-title">Tambah Peminjaman Baru (Langsung Disetujui)</p>
                        <button className="delete" aria-label="close" onClick={() => setIsModalOpen(false)}></button>
                    </header>
                    <section className="modal-card-body">
                        <form onSubmit={handleSubmit}>
                            <div className="field">
                                <label className="label">Nama Anggota</label>
                                <div className="control">
                                    <div className="select is-fullwidth">
                                        <select name="id_anggota" value={formData.id_anggota} onChange={handleChange} required>
                                            <option value="">Pilih Anggota</option>
                                            {anggota.map(a => (
                                                <option key={a.id} value={a.id}>{a.nama_lengkap} ({a.nis})</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>
                            <div className="field">
                                <label className="label">Judul Buku</label>
                                <div className="control">
                                    <div className="select is-fullwidth">
                                        <select name="id_buku" value={formData.id_buku} onChange={handleChange} required>
                                            <option value="">Pilih Buku</option>
                                            {buku.map(b => (
                                                <option key={b.id} value={b.id}>{b.judul}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>
                            <div className="field">
                                <label className="label">Tanggal Peminjaman</label>
                                <div className="control">
                                    <input className="input" type="date" name="tanggal_peminjaman" value={formData.tanggal_peminjaman} onChange={handleChange} required />
                                </div>
                            </div>
                            <div className="field">
                                <label className="label">Tanggal Kembali</label>
                                <div className="control">
                                    <input className="input" type="date" name="tanggal_kembali" value={formData.tanggal_kembali} onChange={handleChange} required />
                                </div>
                            </div>
                            <div className="notification is-info is-light">
                                <p><small>Peminjaman yang dibuat admin akan langsung disetujui (bypass persetujuan)</small></p>
                            </div>
                            <div className="field mt-5">
                                <div className="control">
                                    <button className="button is-info is-fullwidth" type="submit">
                                        Simpan
                                    </button>
                                </div>
                            </div>
                        </form>
                    </section>
                </div>
            </div>

            {/* Modal Reject */}
            <div className={`modal ${isRejectModalOpen ? 'is-active' : ''}`}>
                <div className="modal-background" onClick={() => setIsRejectModalOpen(false)}></div>
                <div className="modal-card">
                    <header className="modal-card-head">
                        <p className="modal-card-title">Tolak Pengajuan Peminjaman</p>
                        <button className="delete" onClick={() => setIsRejectModalOpen(false)}></button>
                    </header>
                    <section className="modal-card-body">
                        <div className="field">
                            <label className="label">Alasan Penolakan *</label>
                            <div className="control">
                                <textarea 
                                    className="textarea"
                                    placeholder="Berikan alasan penolakan yang jelas untuk anggota..."
                                    value={rejectReason}
                                    onChange={(e) => setRejectReason(e.target.value)}
                                    rows="4"
                                    required
                                />
                            </div>
                            <p className="help is-danger">Alasan penolakan wajib diisi dan akan dilihat oleh anggota</p>
                        </div>
                    </section>
                    <footer className="modal-card-foot">
                        <button className="button is-danger" onClick={submitReject}>
                            <FaTimes className="mr-2" />
                            Tolak Peminjaman
                        </button>
                        <button className="button" onClick={() => setIsRejectModalOpen(false)}>
                            Batal
                        </button>
                    </footer>
                </div>
            </div>

            {/* Modal Verifikasi Pengembalian */}
            <div className={`modal ${isVerifikasiModalOpen ? 'is-active' : ''}`}>
                <div className="modal-background" onClick={() => setIsVerifikasiModalOpen(false)}></div>
                <div className="modal-card">
                    <header className="modal-card-head">
                        <p className="modal-card-title">Verifikasi Pengembalian Buku</p>
                        <button className="delete" onClick={() => setIsVerifikasiModalOpen(false)}></button>
                    </header>
                    <section className="modal-card-body">
                        <div className="field">
                            <label className="label">Kondisi Buku</label>
                            <div className="control">
                                <div className="select is-fullwidth">
                                    <select 
                                        value={verifikasiData.kondisi_buku}
                                        onChange={(e) => setVerifikasiData({...verifikasiData, kondisi_buku: e.target.value})}
                                    >
                                        <option value="baik">Baik</option>
                                        <option value="rusak">Rusak</option>
                                        <option value="hilang">Hilang</option>
                                    </select>
                                </div>
                            </div>
                            <p className="help is-info">
                                Pilih kondisi buku saat dikembalikan untuk keperluan laporan
                            </p>
                        </div>
                        
                        <div className="field">
                            <label className="label">Catatan Admin (Opsional)</label>
                            <div className="control">
                                <textarea 
                                    className="textarea"
                                    placeholder="Tambahkan catatan jika diperlukan, misal: halaman 10-15 sobek, cover rusak, dll..."
                                    value={verifikasiData.catatan_admin}
                                    onChange={(e) => setVerifikasiData({...verifikasiData, catatan_admin: e.target.value})}
                                    rows="3"
                                />
                            </div>
                        </div>

                        <div className="notification is-info is-light">
                            <p><strong>Informasi:</strong></p>
                            <ul>
                                <li>• Denda dihitung otomatis berdasarkan keterlambatan (Rp 5.000/hari)</li>
                                <li>• Kondisi buku hanya untuk laporan, tidak mempengaruhi denda</li>
                                <li>• Siswa dengan buku rusak/hilang akan tercatat di laporan</li>
                            </ul>
                        </div>
                    </section>
                    
                    <footer className="modal-card-foot">
                        <button className="button is-success" onClick={handleSubmitVerifikasi}>
                            <i className="fas fa-check mr-2"></i>
                            Verifikasi Pengembalian
                        </button>
                        <button className="button" onClick={() => setIsVerifikasiModalOpen(false)}>
                            Batal
                        </button>
                    </footer>
                </div>
            </div>
        </Layout>
    );
};

export default DataPeminjaman;